import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik/dist/types';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { showToast } from '@app/providers/ToastProvider';
import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { EmailValidationFormValues } from '@components/forms/emailValidation/interfaces';

const NOT_ELIGIBLE_MESSAGE = 'This email address is not eligible for registration. Please contact your administrator.';
const SOMETHING_WENT_WRONG_MESSAGE = 'Something went wrong. Please try again.';
const USER_ALREADY_EXISTS_CODE = 4090;
const USER_ALREADY_EXISTS_MESSAGE = 'A user with this email already exists. Please log in.';

function useEmailPreValidation({ onValidated }: { onValidated: (email: string) => void }) {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();

	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.checkEmail);

	const initialValues: EmailValidationFormValues = {
		email: '',
	};

	const onSubmit = useCallback(
		async (values: EmailValidationFormValues, formikHelpers: FormikHelpers<EmailValidationFormValues>) => {
			try {
				await startRequest({ email: values.email });

				onValidated(values.email);
			} catch (e) {
				const { response } = e as AxiosError<{ errors?: { code: number; description: string }[] }>;
				const errors = response?.data?.errors;

				const userExistsError = errors?.find(error => error.code === USER_ALREADY_EXISTS_CODE);
				if (userExistsError) {
					showToast.info(userExistsError.description || USER_ALREADY_EXISTS_MESSAGE);
					history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
					return;
				}

				const status = response?.status;
				const isRejected = typeof status === 'number' && status >= 400 && status < 500;

				formikHelpers.setFieldError('email', isRejected ? NOT_ELIGIBLE_MESSAGE : SOMETHING_WENT_WRONG_MESSAGE);
			}
		},
		[generateRoutePath, history, onValidated, startRequest],
	);

	return {
		initialValues,
		onSubmit,
	};
}

export default useEmailPreValidation;
